/**
 * useRecorder — MediaRecorder hook with IndexedDB crash recovery.
 * Handles: start, stop, pause, resume, chunk emission, crash protection.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { detectRecorderConfig, isRecordingSupported } from "@/lib/recorder/mediaRecorder";
import { saveChunk, getChunksForSession, assembleChunks, clearSession, hasOrphanedChunks } from "@/lib/recorder/indexedDB";

export type RecorderStatus = "idle" | "requesting" | "recording" | "paused" | "saving" | "error";

const CHUNK_INTERVAL_MS = 30_000; // 30 seconds

export function useWebRecorder(projectId: string) {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasOrphans, setHasOrphans] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sessionIdRef = useRef<string>("");
  const chunkIndexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const config = detectRecorderConfig();
  const isSupported = isRecordingSupported();

  // Check for orphaned chunks on mount
  useEffect(() => {
    hasOrphanedChunks(projectId).then(setHasOrphans);
  }, [projectId]);

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const start = useCallback(async () => {
    if (!config) { setError("Recording not supported in this browser"); return; }
    try {
      setStatus("requesting");
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 48000, noiseSuppression: true, echoCancellation: true, autoGainControl: true },
      });
      streamRef.current = stream;

      // Set up AnalyserNode for waveform
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Create MediaRecorder
      const sessionId = `${projectId}-${Date.now()}`;
      sessionIdRef.current = sessionId;
      chunkIndexRef.current = 0;

      const recorder = new MediaRecorder(stream, {
        mimeType: config.mimeType,
        audioBitsPerSecond: config.audioBitsPerSecond,
      });

      recorder.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          const idx = chunkIndexRef.current++;
          await saveChunk({
            id: `${sessionId}-${idx}`,
            projectId,
            sessionId,
            index: idx,
            blob: e.data,
            mimeType: config.mimeType,
            timestamp: Date.now(),
          });
        }
      };

      recorder.onerror = () => {
        setError("Recording error occurred");
        setStatus("error");
        stopTimer();
      };

      recorderRef.current = recorder;
      recorder.start(CHUNK_INTERVAL_MS);
      setElapsedSeconds(0);
      setStatus("recording");
      startTimer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not access microphone");
      setStatus("error");
    }
  }, [config, projectId, startTimer, stopTimer]);

  const pause = useCallback(() => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.pause();
      setStatus("paused");
      stopTimer();
    }
  }, [stopTimer]);

  const resume = useCallback(() => {
    if (recorderRef.current?.state === "paused") {
      recorderRef.current.resume();
      setStatus("recording");
      startTimer();
    }
  }, [startTimer]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") { resolve(null); return; }

      setStatus("saving");
      stopTimer();

      recorder.onstop = async () => {
        // Stop all tracks
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        recorderRef.current = null;

        // Assemble chunks from IndexedDB
        const chunks = await getChunksForSession(sessionIdRef.current);
        if (chunks.length === 0) { setStatus("idle"); resolve(null); return; }

        const blob = assembleChunks(chunks);
        await clearSession(sessionIdRef.current);
        setStatus("idle");
        setHasOrphans(false);
        resolve(blob);
      };

      recorder.stop();
    });
  }, [stopTimer]);

  const discard = useCallback(async () => {
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    stopTimer();
    await clearSession(sessionIdRef.current);
    setStatus("idle");
    setElapsedSeconds(0);
  }, [stopTimer]);

  // Recovery
  const recoverChunks = useCallback(async (): Promise<Blob | null> => {
    const chunks = await import("@/lib/recorder/indexedDB").then((m) => m.getChunksForProject(projectId));
    if (chunks.length === 0) return null;
    const blob = assembleChunks(chunks);
    return blob;
  }, [projectId]);

  const clearRecovery = useCallback(async () => {
    await import("@/lib/recorder/indexedDB").then((m) => m.clearProject(projectId));
    setHasOrphans(false);
  }, [projectId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [stopTimer]);

  return {
    status, elapsedSeconds, error, isSupported,
    hasOrphanedChunks: hasOrphans,
    mimeType: config?.mimeType ?? "audio/webm",
    fileExtension: config?.fileExtension ?? "webm",
    analyserNode: analyserRef.current,
    start, pause, resume, stop, discard,
    recoverChunks, clearRecovery,
  };
}
